import { useState, useEffect, useMemo, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swords, Pickaxe, Home, Wheat, Server, Users, Search, ArrowRight, LayoutGrid, type LucideIcon } from 'lucide-react';
import { apiService, ServerDTO, OnlineSummaryResponse, OnlinePlayerDTO } from '@/services/api.service';
import { ServerTabs } from '@/components/ServerTabs';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { useActiveGameGroup, useGameGroupsStore } from '@/stores/gameGroups.store';
import { useGameStore } from '@/stores/game.store';
import {
  STANDALONE_FAMILY,
  familyOf,
  groupFamilies,
  groupSuffixes,
  isInfraServer,
  naturalCompare,
  playersByServer,
  prettyName,
  stripGroupSuffix,
} from '@/utils/serverFamilies';
import styles from './ConsolePage.module.scss';

interface FamilyMeta {
  label: string;
  icon: LucideIcon;
  order: number;
}

// Métadonnées d'affichage des familles connues (icône + libellé + ordre).
// Toute autre famille de jeu est détectée dynamiquement (icône générique).
const FAMILY_META: Record<string, FamilyMeta> = {
  faction: { label: 'Faction', icon: Swords, order: 1 },
  mining: { label: 'Minage', icon: Pickaxe, order: 2 },
  lobby: { label: 'Lobby', icon: Home, order: 3 },
  farmland: { label: 'Farmland', icon: Wheat, order: 4 },
};

// Section commune des serveurs seuls de leur famille. En tête quand elle est la plus fournie (sur
// NationsGlory ce sont les serveurs de jeu : Capital, Earth, RP...), sinon en dernier (sur Paladium,
// Faction, Minage et Lobby restent en haut, comme avant).
const STANDALONE_LABEL = 'Serveurs';
const STANDALONE_FIRST = -1;
const STANDALONE_LAST = 1000;

function familyMeta(key: string): FamilyMeta {
  return FAMILY_META[key] ?? { label: prettyName(key), icon: Server, order: 99 };
}

/** Serveur prêt à afficher : nom lisible sans le groupe, famille, icône. */
interface ServerView {
  server: ServerDTO;
  /** « Earth » pour earth-iris, « Lobby 2 » pour lobby-2. */
  displayName: string;
  family: string;
  /** Icône de la section (Lobby pour wonder-lobby), ou de sa propre famille dans « Serveurs ». */
  icon: LucideIcon;
}

interface Section {
  key: string;
  meta: FamilyMeta;
  items: ServerView[];
}

function normalizedStatus(server: ServerDTO): string {
  const status = server.status?.toLowerCase() ?? '';
  return status === 'offline' ? 'stopped' : status;
}

export function ConsolePage() {
  const navigate = useNavigate();
  const [servers, setServers] = useState<ServerDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Groupe de jeu consulté (NationsGlory : Nova, Iris), null sans groupe (Paladium).
  const activeGroup = useActiveGameGroup();
  // Catalogue des groupes du projet : leurs noms sont retirés des noms de serveurs pour les regrouper.
  const activeGameId = useGameStore((s) => s.activeGameId);
  const groupEntry = useGameGroupsStore((s) => s.entries[activeGameId]);

  // Player sync data
  const [playerSummary, setPlayerSummary] = useState<OnlineSummaryResponse | null>(null);
  const [onlinePlayers, setOnlinePlayers] = useState<OnlinePlayerDTO[]>([]);

  useEffect(() => {
    loadServers();
    loadPlayerData();

    // Refresh player data every 15 seconds
    const interval = setInterval(loadPlayerData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadPlayerData = async () => {
    const [summaryResult, playersResult] = await Promise.all([
      apiService.getOnlineSummary(),
      apiService.getOnlinePlayers(),
    ]);

    if (summaryResult.data) {
      setPlayerSummary(summaryResult.data);
    }

    if (playersResult.data) {
      setOnlinePlayers(playersResult.data.players);
    }
  };

  const loadServers = async () => {
    setLoading(true);
    setError(null);

    const result = await apiService.getAllServers();

    if (result.error) {
      console.error('API failed:', result.error);
      setError(`Erreur API: ${result.error}`);
      setServers([]);
    } else if (result.data) {
      // On masque l'infra (proxy/build/cdn...) ; les familles de jeu sont détectées dynamiquement
      const gameServers = result.data.servers.filter((s) => !isInfraServer(s.name));
      console.log('Servers loaded:', gameServers.length);
      setServers(gameServers);
    }

    setLoading(false);
  };

  const handleServerClick = (server: ServerDTO) => {
    navigate(`/console/${server.identifier}`);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return styles.statusRunning;
      case 'stopped':
      case 'offline':
        return styles.statusStopped;
      case 'starting':
        return styles.statusStarting;
      case 'stopping':
        return styles.statusStopping;
      default:
        return styles.statusUnknown;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return 'En ligne';
      case 'stopped':
      case 'offline':
        return 'Arrêté';
      case 'starting':
        return 'Démarrage...';
      case 'stopping':
        return 'Arrêt...';
      default:
        return 'Inconnu';
    }
  };

  // Vue de chaque serveur : nom sans le groupe de jeu (earth-iris -> Earth) et famille. Les suffixes
  // viennent du catalogue des groupes du projet, du groupe actif et du groupe porté par le serveur.
  const views = useMemo(() => {
    const suffixes = groupSuffixes([
      ...(groupEntry?.groups ?? []).flatMap((g) => [g.key, g.label]),
      activeGroup?.key,
      activeGroup?.label,
    ]);
    const stripped = servers.map((server) => {
      const base = stripGroupSuffix(server.name, suffixes, server.group);
      return { server, base, family: familyOf(base) };
    });
    // Deux serveurs au même nom sans groupe (earth-nova et earth-iris sur un satellite qui liste tous
    // les groupes) gardent leur nom complet : deux cartes « Earth » seraient indiscernables.
    const seen = new Map<string, number>();
    for (const { base } of stripped) seen.set(base.toLowerCase(), (seen.get(base.toLowerCase()) ?? 0) + 1);
    return stripped.map(({ server, base, family }) => ({
      server,
      family,
      displayName: prettyName((seen.get(base.toLowerCase()) ?? 0) > 1 ? server.name : base),
    }));
  }, [servers, groupEntry, activeGroup]);

  // Sections calculées sur tous les serveurs : un filtre masque des cartes, il ne déplace jamais un
  // serveur d'une section à l'autre, ni ne change l'ordre des sections.
  const sections = useMemo<Section[]>(() => {
    const groups = groupFamilies(views, (v) => v.family);
    const largest = Math.max(0, ...groups.filter((g) => g.key !== STANDALONE_FAMILY).map((g) => g.items.length));
    return groups
      .map(({ key, items }) => {
        const standalone = key === STANDALONE_FAMILY;
        const meta: FamilyMeta = standalone
          ? {
              label: STANDALONE_LABEL,
              icon: LayoutGrid,
              order: items.length >= largest ? STANDALONE_FIRST : STANDALONE_LAST,
            }
          : familyMeta(key);
        return {
          key,
          meta,
          items: items
            .map((v): ServerView => ({ ...v, icon: standalone ? familyMeta(v.family).icon : meta.icon }))
            .sort((a, b) => naturalCompare(a.displayName, b.displayName)),
        };
      })
      .sort((a, b) => a.meta.order - b.meta.order || naturalCompare(a.meta.label, b.meta.label));
  }, [views]);

  // Joueurs de chaque serveur (indices dans onlinePlayers), d'après la synchro des joueurs en ligne.
  const playersOf = useMemo(() => playersByServer(servers, onlinePlayers), [servers, onlinePlayers]);
  const playerCount = (server: ServerDTO): number => playersOf.get(server.identifier)?.size ?? 0;

  const query = searchQuery.trim().toLowerCase();
  const matches = (view: ServerView): boolean => {
    // Filtre par recherche : nom réel, nom affiché ou identifiant
    const matchesSearch =
      !query ||
      view.server.name.toLowerCase().includes(query) ||
      view.displayName.toLowerCase().includes(query) ||
      view.server.identifier.toLowerCase().includes(query);
    // Filtre par statut (offline = stopped)
    const matchesStatus = statusFilter === 'all' || normalizedStatus(view.server) === statusFilter;
    return matchesSearch && matchesStatus;
  };

  const visibleSections = sections
    .map((section) => ({ ...section, items: section.items.filter(matches) }))
    .filter((section) => section.items.length > 0);
  const visibleCount = visibleSections.reduce((acc, section) => acc + section.items.length, 0);

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, server: ServerDTO) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleServerClick(server);
    }
  };

  // Calculer les stats - use real-time player data for total
  const totalPlayers = playerSummary?.totalPlayers ?? servers.reduce((acc, s) => acc + s.playerCount, 0);
  const onlineServers = servers.filter((s) => s.status?.toLowerCase() === 'running').length;

  if (loading) {
    return (
      <div className={styles.page}>
        <ServerTabs />
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <ServerTabs />
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div>
            <h1 className={styles.title}>Server <GameGroupBadge size="md" /></h1>
            <p className={styles.subtitle}>
              Gérez vos serveurs de jeu en temps réel
            </p>
          </div>
        </div>

        {/* Stats globales */}
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <span className={styles.statIcon}><Server size={22} strokeWidth={2} /></span>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{onlineServers}/{servers.length}</span>
              <span className={styles.statLabel}>Serveurs en ligne</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statIcon}><Users size={22} strokeWidth={2} /></span>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{totalPlayers.toLocaleString()}</span>
              <span className={styles.statLabel}>Joueurs connectés</span>
            </div>
          </div>
        </div>
      </header>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* Filtres */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Rechercher un serveur..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className={styles.statusSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="running">En ligne</option>
          <option value="stopped">Arrêtés</option>
          <option value="starting">En démarrage</option>
        </select>
        <button className={styles.refreshButton} onClick={loadServers}>
          Rafraîchir
        </button>
      </div>

      {/* Serveurs regroupés par famille ; les serveurs seuls de leur famille partagent « Serveurs » */}
      {visibleSections.length > 0 && (
        <div className={styles.families}>
          {visibleSections.map(({ key, items, meta }) => {
            const FamIcon = meta.icon;
            const running = items.filter((v) => normalizedStatus(v.server) === 'running').length;
            // Joueurs distincts : un joueur rapproché de deux cartes ne compte qu'une fois.
            const players = new Set(items.flatMap((v) => [...(playersOf.get(v.server.identifier) ?? [])])).size;
            return (
              <section key={key} className={styles.familySection}>
                <h2 className={styles.familyTitle}>
                  <FamIcon size={16} strokeWidth={2} />
                  <span>{meta.label}</span>
                  <span className={styles.familyCount}>{items.length}</span>
                  <span className={styles.familyStats}>
                    {running}/{items.length} en ligne · {players} joueur{players !== 1 ? 's' : ''}
                  </span>
                </h2>
                <div className={styles.serverGrid}>
                  {items.map(({ server, displayName, icon: ServerIcon }) => {
                    const count = playerCount(server);
                    return (
                      <div
                        key={server.identifier}
                        role="link"
                        tabIndex={0}
                        title={server.description || undefined}
                        className={`${styles.serverCard} ${server.suspended ? styles.suspended : ''}`}
                        onClick={() => handleServerClick(server)}
                        onKeyDown={(e) => handleCardKeyDown(e, server)}
                      >
                        <div className={styles.serverHeader}>
                          <span className={styles.serverIcon}><ServerIcon size={16} strokeWidth={2} /></span>
                          <div className={styles.serverTitleBlock}>
                            <h3 className={styles.serverName}>{displayName}</h3>
                            {/* Nom réel et identifiant Pterodactyl, toujours visibles */}
                            <span className={styles.serverIdentifier}>
                              {server.name} · {server.identifier}
                            </span>
                          </div>
                          <span className={`${styles.statusBadge} ${getStatusColor(server.status)}`}>
                            {getStatusLabel(server.status)}
                          </span>
                        </div>

                        {server.description && (
                          <p className={styles.serverDescription}>{server.description}</p>
                        )}

                        <div className={styles.serverFooter}>
                          <div className={styles.playerCount}>
                            <Users size={14} className={styles.playerIcon} />
                            <span className={styles.playerValue}>
                              {count} joueur{count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {server.suspended ? (
                            <span className={styles.suspendedTag}>Suspendu</span>
                          ) : (
                            <span className={styles.viewDetails}>
                              Voir les détails <ArrowRight size={14} />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Liste en erreur : le bandeau suffit, « modifiez vos filtres » serait faux. */}
      {visibleCount === 0 && !(error && servers.length === 0) && (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}><Search size={32} strokeWidth={1.5} /></span>
          {servers.length === 0 && activeGroup ? (
            // Satellite de groupe : il ne liste que les serveurs de son groupe (RECUBE_GROUP).
            <>
              <p>Aucun serveur dans {activeGroup.label}</p>
              <span className={styles.emptyHint}>
                Un serveur de jeu est rattaché à son groupe par sa variable RECUBE_GROUP dans Pterodactyl.
              </span>
            </>
          ) : servers.length === 0 ? (
            <p>Aucun serveur de jeu</p>
          ) : (
            <>
              <p>Aucun serveur trouvé</p>
              <span className={styles.emptyHint}>
                Essayez de modifier vos filtres de recherche
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
